import { useMemo } from 'react';
import { useProjectAIContext } from '@/hooks/navigation/useProjectIndex';
import { PromptRegistry } from '@/config/prompts/registry';
import { buildFormattingRulesText } from '@/config/prompts/formatting';
import { getProjectIndex } from '@/utils/project-navigation-cache';

// Detection configuration for different recommendation types
const DETECTION_CONFIGS = [
  {
    type: 'task' as const,
    keywords: [
      'task', 'todo', 'action item', 'assignment', 'work on', 'need to do', 
      'should do', 'complete', 'finish', 'accomplish', 'achieve', 'deliver',
      'execute', 'implement', 'handle', 'manage', 'organize', 'plan', 'schedule'
    ],
    contextKeywords: ['project', 'goal', 'milestone', 'deadline', 'priority']
  },
  {
    type: 'research' as const,
    keywords: [
      'research', 'study', 'investigate', 'analyze', 'explore', 'examine',
      'look into', 'find out', 'learn about', 'understand', 'discover',
      'gather information', 'market research', 'competitor analysis'
    ],
    contextKeywords: ['market', 'industry', 'competitor', 'trend', 'data']
  },
  {
    type: 'page' as const,
    keywords: [
      'page', 'document', 'write', 'create content', 'draft', 'article',
      'blog post', 'content', 'copy', 'text', 'documentation', 'report',
      'proposal', 'plan', 'strategy', 'whitepaper', 'case study'
    ],
    contextKeywords: ['content', 'writing', 'document', 'publish', 'share']
  },
  {
    type: 'image' as const,
    keywords: [
      'image', 'picture', 'photo', 'visual', 'graphic', 'illustration',
      'design', 'artwork', 'generate image', 'create visual', 'mockup',
      'logo', 'banner', 'thumbnail', 'cover', 'icon'
    ],
    contextKeywords: ['visual', 'design', 'brand', 'marketing', 'creative']
  },
  {
    type: 'project' as const,
    keywords: [
      'project', 'new project', 'create project', 'start project',
      'goal', 'new goal', 'create goal', 'achieve', 'accomplish',
      'business', 'startup', 'venture', 'initiative', 'vision',
      'launch', 'build', 'develop', 'establish', 'begin'
    ],
    contextKeywords: ['success', 'objective', 'outcome', 'target', 'plan', 'strategy']
  }
] as const;

export interface DetectionType {
  type: 'task' | 'research' | 'page' | 'image' | 'project';
  confidence: number;
  triggeredKeywords: string[];
}

export interface DetectionResult {
  shouldEnhance: boolean;
  detectedTypes: DetectionType[];
  enhancedMessage: string;
}

/**
 * Build simplified context for private library routing
 */
const buildEnhancedProjectContext = (): { basicContext: string } => {
  return {
    basicContext: 'Create tasks, research, pages, and images that will be saved to your private libraries. You can manually assign them to projects later if needed.'
  };
};

/**
 * Templates no longer have placeholders to populate (simplified for private library routing)
 */
const populateTemplate = (template: string, contextData: any): string => {
  return template; // Templates no longer have placeholders to replace
};

export const useSmartDetection = () => {
  const { getContext: getProjectContext } = useProjectAIContext();
  
  const detectRecommendationIntent = useMemo(() => {
    return (message: string): DetectionResult => {
      const lowerMessage = message.toLowerCase();
      const detectedTypes: DetectionType[] = [];
      
      // Split message into words for position analysis
      const words = lowerMessage.split(/\s+/);
      
      // Analyze each detection type and find the best-positioned keyword for each
      for (const config of DETECTION_CONFIGS) {
        const triggeredKeywords: string[] = [];
        let bestScore = 0;
        let bestKeyword = '';
        let bestPosition = Infinity;
        
        // Check each keyword and find the best one (prioritizing earlier position when scores are equal)
        for (const keyword of config.keywords) {
          if (lowerMessage.includes(keyword)) {
            triggeredKeywords.push(keyword);
            
            // Calculate position score (earlier = better)
            const position = words.findIndex(word => word.includes(keyword.split(' ')[0]));
            const positionScore = position === -1 ? 100 : position;
            
            // Update best if this keyword appears earlier or is the first match
            if (positionScore < bestPosition || (positionScore === bestPosition && bestScore === 0)) {
              bestScore = 1;
              bestKeyword = keyword;
              bestPosition = positionScore;
            }
          }
        }
        
        // Check for context keywords to boost confidence
        let contextBoost = 0;
        for (const contextKeyword of config.contextKeywords) {
          if (lowerMessage.includes(contextKeyword)) {
            contextBoost += 0.1;
          }
        }
        
        // Calculate final confidence score
        const confidence = Math.min(bestScore + contextBoost, 1.0);
        
        if (triggeredKeywords.length > 0) {
          detectedTypes.push({
            type: config.type,
            confidence,
            triggeredKeywords,
            bestScore, // Store for comparison
            bestPosition // Store for comparison
          } as any);
        }
      }
      
      // Sort by confidence (desc), then by position (asc)
      detectedTypes.sort((a, b) => {
        const aScore = (a as any).bestScore || a.confidence;
        const bScore = (b as any).bestScore || b.confidence;
        const aPos = (a as any).bestPosition || Infinity;
        const bPos = (b as any).bestPosition || Infinity;
        
        if (bScore !== aScore) return bScore - aScore;
        return aPos - bPos;
      });
      
      // Only enhance if we have confident detections
      const shouldEnhance = detectedTypes.length > 0 && detectedTypes[0].confidence >= 0.3;
      
      let enhancedMessage = message;
      
      if (shouldEnhance) {
        // Choose the detection type with the highest position-weighted score (prefer earlier position when tied)
        const highestScoring = detectedTypes.reduce((prev, current) => {
          const prevScore = (prev as any).bestScore || prev.confidence;
          const currentScore = (current as any).bestScore || current.confidence;
          const prevPosition = (prev as any).bestPosition || Infinity;
          const currentPosition = (current as any).bestPosition || Infinity;
          
          // Prefer higher score, then earlier position
          if (currentScore > prevScore) {
            return current;
          } else if (currentScore === prevScore && currentPosition < prevPosition) {
            return current;
          }
          return prev;
        });
        
        // Get simplified context for private library routing
        const contextData = buildEnhancedProjectContext();
        
        // Build enhancement prompt with only the single highest scoring type using registry
        const template = PromptRegistry.getTemplate(highestScoring.type);
        const formattingRules = buildFormattingRulesText();
        
        if (template) {
          // Templates no longer need placeholder population
          const populatedTemplate = populateTemplate(template, contextData);
          
          enhancedMessage = `${message}\n\n---\n\nCONTEXT:\n${contextData.basicContext}\n\n---\n\n${populatedTemplate}${formattingRules}`;
        }
      }
      
      return {
        shouldEnhance,
        detectedTypes,
        enhancedMessage
      };
    };
  }, [getProjectContext]);

  return {
    detectRecommendationIntent
  };
}; 